import SectionFormModal from "@/components/menu/SectionFormModal";
import { useModal } from "@/lib/hooks";
import { cn, getImageUrl } from "@/lib/utils";
import {
  useDeleteSectionMutation,
  useGetMenuByIdQuery,
} from "@/redux/services/menu/menu.api";
import { Section } from "@/redux/services/menu/menu.type";
import { Pencil, Trash2 } from "lucide-react";
import React from "react";
import toast from "react-hot-toast";
import { useConfirm } from "@/components/ui/confirm-dialog";

interface Props {
  menuId: string;
  section: Section;
  selectedSection?: Section;
  handleSectionClick: (section: Section) => void;
  isReadOnly?: boolean;
}

const SectionItemCard: React.FC<Props> = (props) => {
  const { section, selectedSection, handleSectionClick, menuId, isReadOnly } =
    props;
  const { refetch } = useGetMenuByIdQuery(menuId);

  const [deleteSection] = useDeleteSectionMutation();
  const confirm = useConfirm();

  const {
    item: sectionForEdit,
    isOpen: isSectionFormOpen,
    openEditModal: openSectionEditForm,
    closeModal: closeSectionForm,
  } = useModal<Section>();

  const handleDeleteSection = async (section: Section) => {
    const ok = await confirm({
      title: "Êtes-vous sûr?",
      description: `Voulez-vous vraiment supprimer la section "${section.title}"?`,
      confirmText: "Supprimer",
      cancelText: "Annuler",
      variant: "destructive",
      icon: <Trash2 className="h-6 w-6" />,
    });
    if (!ok) return;
    try {
      await deleteSection(section.id).unwrap();
      toast.success("Section supprimée");
      refetch();
    } catch (error) {
      toast.error("Erreur lors de la suppression de la section");
    }
  };

  const isSelected = selectedSection?.id === section.id;
  const itemCount = section.menuItems?.length ?? 0;

  return (
    <>
      <div
        className={cn(
          "flex items-center gap-2 py-2 px-3 rounded-full border transition-all text-sm whitespace-nowrap",
          isSelected
            ? "border-primary bg-primary/5 text-primary font-medium"
            : "border-border bg-card text-muted-foreground hover:border-primary/30"
        )}
      >
        <button
          className="flex items-center gap-2"
          onClick={() => handleSectionClick(section)}
        >
          <img
            src={getImageUrl(section?.image?.url)}
            alt={section?.title}
            className="w-6 h-6 object-cover rounded-full flex-shrink-0"
          />
          <span>{section.title} ({itemCount})</span>
        </button>

        {!isReadOnly && (
          <>
            <span className="w-px h-4 bg-border" />
            <div className="flex gap-0.5">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openSectionEditForm(section);
                }}
                className="p-0.5 text-muted-foreground hover:text-success transition-colors"
                title="Modifier la section"
              >
                <Pencil size={12} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteSection(section);
                }}
                className="p-0.5 text-muted-foreground hover:text-destructive transition-colors"
                title="Supprimer la section"
              >
                <Trash2 size={12} />
              </button>
            </div>
          </>
        )}
      </div>

      <SectionFormModal
        isOpen={isSectionFormOpen}
        onClose={closeSectionForm}
        section={sectionForEdit}
        menuId={menuId}
      />
    </>
  );
};

export const SectionItemCardSkeleton = () => {
  return (
    <div className="flex items-center gap-2 py-2 px-3 rounded-full border border-border animate-pulse">
      <div className="w-6 h-6 bg-gray-200 rounded-full" />
      <div className="w-16 h-4 bg-gray-200 rounded" />
    </div>
  );
};

export default SectionItemCard;
